// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { create } from 'zustand'

export const STORAGE_KEY   = 'wilsapp_wizard_step'
export const COMPLETED_KEY = 'wilsapp_wizard_done'
export const ANSWERS_KEY   = 'wilsapp_wizard_answers'
export const WIZARD_VERSION_KEY = 'wilsapp_wizard_version'
const WIZARD_VERSION = 2

// V1 (19 stappen) → V2 (23 stappen): eenmalige hermapping van de opgeslagen stappositie.
// Antwoordvlaggen en moduledata zijn op naam/sleutel opgeslagen en migreren vanzelf mee.
const STEP_MIGRATION_V1_TO_V2: Record<number, number> = {
  0: 0, 1: 1, 2: 3, 3: 4, 4: 5, 5: 6, 6: 7, 7: 8, 8: 11, 9: 10,
  10: 2, 11: 12, 12: 13, 13: 14, 14: 15, 15: 16, 16: 17, 17: 21, 18: 22,
}

function getSavedStep(): number {
  const raw = localStorage.getItem(STORAGE_KEY)
  const version = parseInt(localStorage.getItem(WIZARD_VERSION_KEY) || '1', 10)

  // Nieuwe gebruiker: niets te migreren. Markeer huidige versie zonder STORAGE_KEY te zetten
  // (zodat de welkomst-/hasStarted-logica intact blijft).
  if (raw === null) {
    localStorage.setItem(WIZARD_VERSION_KEY, String(WIZARD_VERSION))
    return 0
  }

  const n = parseInt(raw, 10)
  const step = isNaN(n) ? 0 : Math.max(0, n)

  // Eenmalige migratie V1 → V2
  if (version < WIZARD_VERSION) {
    const mapped = STEP_MIGRATION_V1_TO_V2[step] ?? Math.min(step, 22)
    localStorage.setItem(STORAGE_KEY, String(mapped))
    localStorage.setItem(WIZARD_VERSION_KEY, String(WIZARD_VERSION))
    return mapped
  }

  return step
}

function getSavedAnswers(): WizardAnswers {
  try {
    const s = localStorage.getItem(ANSWERS_KEY)
    return s ? JSON.parse(s) : {}
  } catch { return {} }
}

export interface WizardAnswers {
  hasChildren?:      boolean
  hasPets?:          boolean
  hasOwnHome?:       boolean
  hasSecondHome?:    boolean
  hasBusiness?:      boolean
  hasInvestments?:   boolean
  donorStatus?:      'yes' | 'no' | 'unknown'
  hasTestament?:     boolean
  hasLivingWill?:    boolean
  hasFuneralWishes?: boolean
  firstName?:        string
}

interface WizardState {
  currentStep:  number
  totalSteps:   number
  isCompleted:  boolean
  answers:      WizardAnswers

  setStep:       (step: number) => void
  nextStep:      () => void
  prevStep:      () => void
  markCompleted: () => void
  setAnswer:     <K extends keyof WizardAnswers>(key: K, value: WizardAnswers[K]) => void
  reset:         () => void  // Full reset: localStorage + in-memory state → step 0
}

export const useWizardStore = create<WizardState>((set, get) => ({
  currentStep: getSavedStep(),
  totalSteps:  23,
  isCompleted: localStorage.getItem(COMPLETED_KEY) === 'true',
  answers:     getSavedAnswers(),

  setStep: (step) => {
    const clamped = Math.max(0, Math.min(step, get().totalSteps - 1))
    localStorage.setItem(STORAGE_KEY, String(clamped))
    set({ currentStep: clamped })
  },

  nextStep: () => {
    const { currentStep, totalSteps, setStep } = get()
    if (currentStep < totalSteps - 1) setStep(currentStep + 1)
  },

  prevStep: () => {
    const { currentStep, setStep } = get()
    if (currentStep > 0) setStep(currentStep - 1)
  },

  markCompleted: () => {
    localStorage.setItem(COMPLETED_KEY, 'true')
    set({ isCompleted: true })
  },

  setAnswer: (key, value) => {
    const updated = { ...get().answers, [key]: value }
    localStorage.setItem(ANSWERS_KEY, JSON.stringify(updated))
    set({ answers: updated })
  },

  // Full synchronous reset: wipes localStorage AND in-memory state simultaneously
  reset: () => {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(COMPLETED_KEY)
    localStorage.removeItem(ANSWERS_KEY)
    set({ currentStep: 0, isCompleted: false, answers: {} })
  },
}))

/**
 * Standalone helper - wipes localStorage without needing the store hook.
 * Always call useWizardStore.getState().reset() instead of this directly,
 * unless you are outside a React context.
 */
export function hardResetWizard() {
  localStorage.removeItem(STORAGE_KEY)
  localStorage.removeItem(COMPLETED_KEY)
  localStorage.removeItem(ANSWERS_KEY)
  // Also push the in-memory store to 0 synchronously
  useWizardStore.setState({ currentStep: 0, isCompleted: false, answers: {} })
}
